import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import type { Analytics as FbAnalytics } from 'firebase/analytics';
import {
  getAnalytics as rawGetAnalytics,
  initializeAnalytics as rawInitializeAnalytics,
  isSupported as rawIsSupported,
  logEvent as rawLogEvent,
  setAnalyticsCollectionEnabled as rawSetAnalyticsCollectionEnabled,
  setConsent as rawSetConsent,
  setCurrentScreen as rawSetCurrentScreen,
  setDefaultEventParameters as rawSetDefaultEventParameters,
  setUserId as rawSetUserId,
  setUserProperties as rawSetUserProperties,
  settings as rawSettings,
} from 'firebase/analytics';
import { kdvWrap } from '@fido/firebase';
import { FirebaseApp } from '@fido/firebase/app';
import { Analytics } from './lib/tokens';

// 1) Wrapped firebase/analytics functions. blockUntilFirst values mirror
//    @angular/fire's _zoneWrap declarations from angular-fire-analytics.mjs:
//    isSupported and logEvent use `false` (one-shot or fire-and-forget);
//    everything else uses `true`.
export const getAnalytics = kdvWrap(rawGetAnalytics, true);
export const initializeAnalytics = kdvWrap(rawInitializeAnalytics, true);
export const isSupported = kdvWrap(rawIsSupported, false);
export const logEvent = kdvWrap(rawLogEvent, false);
export const setAnalyticsCollectionEnabled = kdvWrap(rawSetAnalyticsCollectionEnabled, true);
export const setConsent = kdvWrap(rawSetConsent, true);
export const setCurrentScreen = kdvWrap(rawSetCurrentScreen, true);
export const setDefaultEventParameters = kdvWrap(rawSetDefaultEventParameters, true);
export const setUserId = kdvWrap(rawSetUserId, true);
export const setUserProperties = kdvWrap(rawSetUserProperties, true);
export const settings = kdvWrap(rawSettings, true);

// 2) DI token (declaration-merged with type alias). Same name as
//    @angular/fire so consumer call sites like `inject(Analytics)` keep
//    compiling unchanged.
export { Analytics } from './lib/tokens';

export function provideAnalytics(factory: () => FbAnalytics): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: Analytics,
      // FirebaseApp dep forces the underlying initializeApp() to run before
      // this factory — same ordering guarantee as @angular/fire's
      // DEFAULT_ANALYTICS_INSTANCE_PROVIDER. The injected app is unused; we
      // keep the user's no-arg factory contract.
      useFactory: (_app: unknown) => factory(),
      deps: [FirebaseApp],
    },
  ]);
}

// 3) Tracking services — ported from @angular/fire/analytics.
export { ScreenTrackingService } from './lib/screen-tracking.service';
export { UserTrackingService } from './lib/user-tracking.service';

// 4) Internal screen_view payload builder + the event-key constants.
//    Not used by fido today; preserved on the surface for parity with
//    @angular/fire's ɵscreenViewEvent export.
export { screenViewEvent, type ScreenViewParams } from './lib/screen-view-event';
export {
  EVENT_ORIGIN_AUTO,
  FIREBASE_EVENT_ORIGIN_KEY,
  FIREBASE_PREVIOUS_SCREEN_CLASS_KEY,
  FIREBASE_PREVIOUS_SCREEN_INSTANCE_ID_KEY,
  FIREBASE_PREVIOUS_SCREEN_NAME_KEY,
  FIREBASE_SCREEN_CLASS_KEY,
  FIREBASE_SCREEN_INSTANCE_ID_KEY,
  FIREBASE_SCREEN_NAME_KEY,
  OUTLET_KEY,
  PAGE_PATH_KEY,
  PAGE_TITLE_KEY,
  SCREEN_CLASS_KEY,
  SCREEN_INSTANCE_DELIMITER,
  SCREEN_NAME_KEY,
  SCREEN_VIEW_EVENT,
} from './lib/screen-view-event';
